import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, RefreshCw, User, Phone, MapPin, Package, DollarSign, Truck, CheckCircle, Clock, X, Eye, Download } from 'lucide-react';
import { useToast } from '../contexts/ToastContext';
import MainService from '../services/mainservice';
import * as XLSX from 'xlsx';
import { useLanguage } from '../contexts/LanguageContext';

const TrackByCustomer = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [orders, setOrders] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [skip, setSkip] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [allOrders, setAllOrders] = useState([]); // For Excel export
  const [hasSearched, setHasSearched] = useState(false); // Track if search has been performed
  const observerRef = useRef();
  const { showSuccess, showError, showWarning } = useToast();

  const handleSearch = useCallback(async (reset = true) => {
    if (!searchQuery.trim()) {
      showWarning('Please enter customer name or mobile number');
      return;
    }

    try {
      setHasSearched(true);
      if (reset) {
        setIsLoading(true);
        setSkip(0);
        setOrders([]);
        setAllOrders([]);
        setHasMore(true);
      } else {
        setIsLoadingMore(true);
      }
      
      const tokenData = localStorage.getItem('AINSOFT_DRIVER_TOKEN');
      if (!tokenData) {
        showError('Please login first');
        return;
      }

      const userData = JSON.parse(tokenData);
      const data = {
        Keyword: searchQuery.trim(),
        ClientId: userData.ClientId,
        Skip: reset ? 0 : skip,
        Take: 20
      };

      const response = await MainService.TrackOrdersByCustomerInPortal(data, userData.Token);
      
      if (response && response.Orders && response.Orders.length > 0) {
        if (reset) {
          setOrders(response.Orders);
          setAllOrders(response.Orders);
          setTotalCount(response.TotalCount || response.Orders.length);
          showSuccess(`Found ${response.TotalCount || response.Orders.length} order(s) for "${searchQuery}"`);
        } else {
          setOrders(prev => [...prev, ...response.Orders]);
          setAllOrders(prev => [...prev, ...response.Orders]);
        }
        setSkip(prev => prev + 20);
        setHasMore(response.Orders.length === 20);
      } else {
        if (reset) {
          setOrders([]);
          setAllOrders([]);
          setTotalCount(0);
          showWarning(`No orders found for "${searchQuery}"`);
        }
        setHasMore(false);
      }
    } catch (error) {
      console.error('Error searching orders:', error);
      showError('Network error. Please try again.');
    } finally {
      setIsLoading(false);
      setIsLoadingMore(false);
    }
  }, [searchQuery, skip, showWarning, showError, showSuccess]);

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  // Infinite scroll observer
  const lastOrderElementRef = useCallback(node => {
    if (isLoadingMore) return;
    if (observerRef.current) observerRef.current.disconnect();
    observerRef.current = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting && hasMore && searchQuery.trim()) {
        handleSearch(false);
      }
    });
    if (node) observerRef.current.observe(node);
  }, [isLoadingMore, hasMore, searchQuery, handleSearch]);

  // Excel export function
  const exportToExcel = () => {
    if (allOrders.length === 0) {
      showWarning('No orders to export');
      return;
    }

    try {
      const exportData = allOrders.map((order, index) => ({
        '#': index + 1,
        'Voucher No': order.VoucherNo || 'N/A',
        'Client Bill No': order.ClientBillNo || 'N/A',
        'Customer Name': order.Customer?.Name || 'N/A',
        'Mobile No1': order.Customer?.MobileNo1 || 'N/A',
        'Mobile No2': order.Customer?.MobileNo2 || 'N/A',
        'Status': order.Status?.Name || 'N/A',
        'Address': order.CustomerAddress?.Address || 'N/A',
        'Location': order.CustomerAddress?.Location?.Name || 'N/A',
        'Emirate': order.CustomerAddress?.Emirate?.Name || 'N/A',
        'Order Date': order.Date ? new Date(order.Date).toLocaleDateString('en-GB') : 'N/A',
        'Pickup Date': order.PickupDate ? new Date(order.PickupDate).toLocaleDateString('en-GB') : 'N/A',
        'Delivery Date': order.DeliveryDate ? new Date(order.DeliveryDate).toLocaleDateString('en-GB') : 'N/A',
        'Product Price': order.ProductPrice ? `AED ${parseFloat(order.ProductPrice).toFixed(2)}` : 'N/A',
        'Delivery Charge': order.DeliveryCharge ? `AED ${parseFloat(order.DeliveryCharge).toFixed(2)}` : 'N/A',
        'Tax': order.TAX ? `AED ${parseFloat(order.TAX).toFixed(2)}` : 'N/A',
        'Collection Amount': order.CustomerAmount ? `AED ${parseFloat(order.CustomerAmount).toFixed(2)}` : 'N/A',
        'My Payment': order.ClientAmount ? `AED ${parseFloat(order.ClientAmount).toFixed(2)}` : 'N/A'
      }));

      const ws = XLSX.utils.json_to_sheet(exportData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Search Results');
      
      const fileName = `search_results_${new Date().toISOString().split('T')[0]}.xlsx`;
      XLSX.writeFile(wb, fileName);
      
      showSuccess('Excel file exported successfully');
    } catch (error) {
      console.error('Export error:', error);
      showError('Failed to export to Excel');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-GB');
  };

  const formatCurrency = (amount) => {
    if (!amount) return 'N/A';
    return `AED ${parseFloat(amount).toFixed(2)}`;
  };



  if (isLoading) {
    return (
      <div className="page-container flex items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
          <p className="text-primary-600">Searching orders...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container bg-gradient-to-br from-gray-50 to-gray-100">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">{t('trackCustomer.title')}</h1>
          <p className="text-gray-600">{t('trackCustomer.subtitle')}</p>
        </div>

        {/* Search Section */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
          <div className="flex flex-col lg:flex-row gap-4">
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Customer Search
              </label>
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder={t('trackCustomer.placeholder')}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>
            <div className="flex items-end gap-3">
              <button
                onClick={handleSearch}
                className="bg-green-500 hover:bg-green-600 text-white font-medium py-3 px-6 rounded-lg transition-all duration-200 flex items-center space-x-2"
              >
                <Search className="w-4 h-4" />
                <span>{t('common.search')}</span>
              </button>
              <button
                onClick={() => {
                  setSearchQuery('');
                  setOrders([]);
                  setAllOrders([]);
                  setHasSearched(false);
                  setSkip(0);
                  setHasMore(true);
                  setTotalCount(0);
                }}
                className="bg-gray-500 hover:bg-gray-600 text-white font-medium py-3 px-6 rounded-lg transition-all duration-200 flex items-center space-x-2"
              >
                <RefreshCw className="w-4 h-4" />
                <span>{t('common.reset')}</span>
              </button>
            </div>
          </div>
        </div>

        {/* Results Section */}
        {hasSearched ? (
          <div className="space-y-6">
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-semibold text-gray-900">
                  {t('trackCustomer.results', { count: totalCount })}
                </h2>
                <div className="flex items-center space-x-3">
                  <button
                    onClick={exportToExcel}
                    className="bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded-lg flex items-center space-x-2 transition-colors duration-200"
                  >
                    <Download className="w-4 h-4" />
                    <span>{t('common.exportExcel')}</span>
                  </button>
                </div>
              </div>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="bg-gray-100 border-b border-gray-200">
                        <th className="text-center py-4 px-4 text-xs font-semibold text-gray-600 uppercase tracking-wider">#</th>
                        <th className="text-left py-4 px-4 text-xs font-semibold text-gray-600 uppercase tracking-wider">VOUCHER NO</th>
                        <th className="text-left py-4 px-4 text-xs font-semibold text-gray-600 uppercase tracking-wider">CLIENT BILL</th>
                        <th className="text-left py-4 px-4 text-xs font-semibold text-gray-600 uppercase tracking-wider">CUSTOMER</th>
                        <th className="text-left py-4 px-4 text-xs font-semibold text-gray-600 uppercase tracking-wider">STATUS</th>
                        <th className="text-left py-4 px-4 text-xs font-semibold text-gray-600 uppercase tracking-wider">ADDRESS</th>
                        <th className="text-left py-4 px-4 text-xs font-semibold text-gray-600 uppercase tracking-wider">LOCATION</th>
                        <th className="text-left py-4 px-4 text-xs font-semibold text-gray-600 uppercase tracking-wider">EMIRATE</th>
                        <th className="text-left py-4 px-4 text-xs font-semibold text-gray-600 uppercase tracking-wider">ORDER DATE</th>
                        <th className="text-left py-4 px-4 text-xs font-semibold text-gray-600 uppercase tracking-wider">DELIVERY DATE</th>
                        <th className="text-left py-4 px-4 text-xs font-semibold text-gray-600 uppercase tracking-wider">PRODUCT PRICE</th>
                        <th className="text-left py-4 px-4 text-xs font-semibold text-gray-600 uppercase tracking-wider">DELIVERY CHARGE</th>
                        <th className="text-left py-4 px-4 text-xs font-semibold text-gray-600 uppercase tracking-wider">TAX</th>
                        <th className="text-left py-4 px-4 text-xs font-semibold text-gray-600 uppercase tracking-wider">COLLECTION AMOUNT</th>
                        <th className="text-left py-4 px-4 text-xs font-semibold text-gray-600 uppercase tracking-wider">MY PAYMENT</th>
                        <th className="text-left py-4 px-4 text-xs font-semibold text-gray-600 uppercase tracking-wider">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {orders.length > 0 ? (
                        orders.map((order, index) => (
                          <tr 
                            key={index} 
                            className="border-b border-gray-200 hover:bg-gray-50 transition-colors duration-200"
                            ref={index === orders.length - 1 ? lastOrderElementRef : null}
                          >
                            <td className="py-4 px-4 text-sm text-center text-gray-500 font-medium">
                              {index + 1}
                            </td>
                            <td className="py-4 px-4 text-sm font-medium text-gray-900">
                              <div className="flex items-center">
                                <div className="w-2 h-2 bg-green-500 rounded-full mr-2"></div>
                                {order.VoucherNo || 'N/A'}
                              </div>
                            </td>
                            <td className="py-4 px-4 text-sm text-gray-700">{order.ClientBillNo || 'N/A'}</td>
                            <td className="py-4 px-4">
                              <div>
                                <div className="text-sm font-medium text-gray-900">{order.Customer?.Name || 'N/A'}</div>
                                <div className="text-xs text-gray-500">{order.Customer?.MobileNo1 || 'N/A'}</div>
                              </div>
                            </td>
                            <td className="py-4 px-4">
                              <span className="inline-flex px-2 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-800">
                                {order.Status?.Name || 'N/A'}
                              </span>
                            </td>
                            <td className="py-4 px-4 text-sm text-gray-700 max-w-xs truncate">
                              {order.CustomerAddress?.Address || 'N/A'}
                            </td>
                            <td className="py-4 px-4 text-sm text-gray-700">
                              {order.CustomerAddress?.Location?.Name || 'N/A'}
                            </td>
                            <td className="py-4 px-4 text-sm text-gray-700">
                              {order.CustomerAddress?.Emirate?.Name || 'N/A'}
                            </td>
                            <td className="py-4 px-4 text-sm text-gray-700">
                              {formatDate(order.Date)}
                            </td>
                            <td className="py-4 px-4 text-sm text-gray-700">
                              {formatDate(order.DeliveryDate)}
                            </td>
                            <td className="py-4 px-4 text-sm font-semibold text-gray-900">
                              {formatCurrency(order.ProductPrice)}
                            </td>
                            <td className="py-4 px-4 text-sm text-gray-700">
                              {formatCurrency(order.DeliveryCharge)}
                            </td>
                            <td className="py-4 px-4 text-sm text-gray-700">
                              {formatCurrency(order.TAX)}
                            </td>
                            <td className="py-4 px-4 text-sm text-gray-700">
                              {formatCurrency(order.CustomerAmount)}
                            </td>
                            <td className="py-4 px-4 text-sm font-bold text-green-600">
                              {formatCurrency(order.ClientAmount)}
                            </td>
                            <td className="py-4 px-4">
                              <button
                                onClick={() => navigate(`/order-details/${order.Id}`)}
                                className="p-1 text-primary-600 hover:text-primary-700 hover:bg-primary-50 rounded transition-colors duration-200"
                                title="View Order Details"
                              >
                                <Eye className="w-4 h-4" />
                              </button>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan="16" className="py-8 px-4 text-center text-gray-500">
                            <div className="flex flex-col items-center">
                              <User className="w-8 h-8 text-gray-400 mb-2" />
                              <p>No orders found for "{searchQuery}"</p>
                            </div>
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              
              {/* Scroll Down Message */}
              {hasMore && orders.length > 0 && !isLoadingMore && (
                <div className="text-center py-4 text-gray-500 text-sm bg-blue-50 border border-blue-200 rounded-lg mx-4">
                  <div className="flex items-center justify-center space-x-2">
                    <div className="w-2 h-2 bg-blue-500 rounded-full animate-bounce"></div>
                    <span>Scroll down to load more orders...</span>
                    <div className="w-2 h-2 bg-blue-500 rounded-full animate-bounce" style={{animationDelay: '0.1s'}}></div>
                  </div>
                </div>
              )}

              {/* Loading More Indicator */}
              {isLoadingMore && (
                <div className="flex items-center justify-center py-4">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600 mr-2"></div>
                  <span className="text-gray-600">Loading more orders...</span>
                </div>
              )}
              
              {/* End of Results */}
              {!hasMore && orders.length > 0 && !isLoadingMore && (
                <div className="text-center py-4 text-gray-500 text-sm">
                  No more orders to load
                </div>
              )}
            </div>
          </div>
        ) : (
          /* Message Box when no orders */
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <User className="w-8 h-8 text-blue-500" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">{t('trackCustomer.title')}</h3>
              <p className="text-gray-600 mb-6 max-w-md mx-auto">
                Enter customer name or mobile number to find their orders. You can search by partial names or phone numbers.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <div className="bg-gray-50 rounded-lg p-4 text-left">
                  <h4 className="font-medium text-gray-900 mb-2">Search Examples:</h4>
                  <ul className="text-sm text-gray-600 space-y-1">
                    <li>• Customer name: "John Doe"</li>
                    <li>• Partial name: "John" or "Doe"</li>
                    <li>• Mobile number: "0501234567"</li>
                    <li>• Partial mobile: "050123"</li>
                  </ul>
                </div>
                <div className="bg-gray-50 rounded-lg p-4 text-left">
                  <h4 className="font-medium text-gray-900 mb-2">What you'll see:</h4>
                  <ul className="text-sm text-gray-600 space-y-1">
                    <li>• All orders for the customer</li>
                    <li>• Order status and dates</li>
                    <li>• Delivery addresses</li>
                    <li>• Payment information</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TrackByCustomer;
